import React, { useCallback, useEffect, useState } from 'react'
import { Banner } from '@astryxdesign/core/Banner'
import PillButton from './PillButton'

const WARN_AT_PERCENT = 80

// The data behind this banner is a daily total, so catching a threshold crossing within a couple
// of minutes is plenty -- this does not need to be aggressive. Exported so the test can advance
// fake timers by the exact value this component uses, rather than a duplicated magic number that
// could silently drift from it.
export const REFRESH_INTERVAL_MS = 2 * 60 * 1000

interface Quota {
  used: number
  limit: number
  remaining: number
  percent: number
  resetsAt: string
}

/**
 * Warns a writer approaching their daily allowance, so the 429 at 100% is never the first
 * they hear of it. Silent below the threshold, and silent on any error: a failed quota read
 * must never put a scary banner over someone's manuscript.
 *
 * Refetches on mount, when the writer switches story, when the tab regains focus, and on a
 * REFRESH_INTERVAL_MS poll while mounted. The poll is what covers a long session that never
 * loses focus at all -- exactly the case the 80% warning exists for, and the one a focus/mount/
 * story-switch trigger alone silently misses. Deliberately not driven by a counter the workspace
 * bumps after each turn: the obvious source there is a ref, and reading a ref during render is a
 * react-hooks/refs violation -- and threading fresh state through App.tsx (already ~3,700 lines)
 * for what a cheap poll already covers isn't a trade worth making.
 */
export default function UsageQuotaBanner({
  storyId
}: {
  storyId?: string | null
}): React.JSX.Element | null {
  const [quota, setQuota] = useState<Quota | null>(null)
  const [dismissed, setDismissed] = useState(false)

  // Takes the signal as a parameter (rather than closing over one `useState`-held
  // AbortController) so each mount/focus/story-switch gets its own controller, tied to that
  // one request's lifetime -- see the effect below.
  const load = useCallback((signal: AbortSignal): void => {
    fetch('/api/usage/quota', { signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data) setQuota(data as Quota)
      })
      .catch((err: unknown) => {
        // Aborting (story switch or unmount while the request was in flight) rejects the fetch
        // with an AbortError -- expected, not a failure, so it's named here rather than falling
        // into the same bucket as a real network/parse error by accident. Both branches end up
        // silent regardless (see the component comment), so this doesn't change behaviour today;
        // it documents that the abort case was considered, not just incidentally swallowed.
        if (err instanceof DOMException && err.name === 'AbortError') return
      })
  }, [])

  useEffect(() => {
    // AbortController so a request from a story we've since left (or a component that's since
    // unmounted) never lands a stale setQuota. Cancelled the same way the focus listener and the
    // poll interval are torn down: in this effect's own cleanup.
    const controller = new AbortController()
    const onFocus = () => load(controller.signal)
    window.addEventListener('focus', onFocus)
    onFocus()
    const intervalId = setInterval(() => load(controller.signal), REFRESH_INTERVAL_MS)
    return () => {
      controller.abort()
      window.removeEventListener('focus', onFocus)
      clearInterval(intervalId)
    }
  }, [load, storyId])

  if (!quota || dismissed || quota.percent < WARN_AT_PERCENT) return null

  const resets = new Date(quota.resetsAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  })

  return (
    <Banner
      status="warning"
      title={`You have used ${quota.percent}% of today's writing allowance`}
      description={`About ${quota.remaining.toLocaleString()} tokens left. Resets at ${resets}.`}
      endContent={
        <PillButton label="Dismiss" variant="ghost" size="sm" onClick={() => setDismissed(true)} />
      }
    />
  )
}
